/**
 * Interface Admin - Gestion Avancée des Payins (Commissions Propriétaires)
 * Dashboard complet avec métriques, filtres, table et actions administratives
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  TextField,
  Paper,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  Loop as LoopIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Note as NotesIcon,
  Add as AddIcon,
  Euro as EuroIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useAuth } from '../../context/AuthContext';
import adminPayinService from '../../services/adminPayinService';

// Configuration française pour dayjs
dayjs.locale('fr');

const AdminPayins = () => {
  const { user } = useAuth();
  
  // ================================
  // États de base
  // ================================
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [payinsData, setPayinsData] = useState(null);
  const [error, setError] = useState(null);

  // ================================
  // États des filtres
  // ================================
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'day'),
    endDate: dayjs(),
    status: '',
    ownerId: '',
    minAmount: '',
    maxAmount: '',
    overdue_only: false,
    search: ''
  });

  // ================================
  // États de pagination infinie
  // ================================
  const [loadedPayins, setLoadedPayins] = useState([]);
  const [groupedPayins, setGroupedPayins] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [pageSize] = useState(10); // Charger 10 payins à la fois
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // ================================
  // États d'expansion des groupes
  // ================================
  const [expandedOwners, setExpandedOwners] = useState(new Set());

  // ================================
  // États des dialogues
  // ================================
  const [paymentDialog, setPaymentDialog] = useState({ open: false, payin: null });
  const [notesDialog, setNotesDialog] = useState({ open: false, payin: null, notes: '' });
  const [manualDialog, setManualDialog] = useState({ open: false });

  // ================================
  // Données pour dialogue manuel
  // ================================
  const [manualPayinData, setManualPayinData] = useState({
    ownerId: '',
    reservationId: '',
    commissionAmount: '',
    taxes: '',
    currency: 'EUR',
    payinType: 'commission',
    dueDate: dayjs(),
    notes: '',
    cashAmountReceived: ''
  });

  // ================================
  // Fonctions helpers pour multi-devises
  // ================================
  
  /**
   * Formate un montant multi-devises avec détails par devise
   * @param {Object} currencyData - Données par devise {by_currency: {...}, total_converted: number}
   * @param {boolean} showBreakdown - Afficher le détail par devise
   * @returns {Object} {primary: string, breakdown: Array}
   */
  const formatMultiCurrencyAmount = (currencyData, showBreakdown = false) => {
    if (!currencyData) {
      return { primary: '0,00 €', breakdown: [] };
    }

    // Montant principal en devise de base
    const primaryAmount = adminPayinService.formatAmount(currencyData.total_converted || 0);
    
    // Détail par devise
    const breakdown = [];
    if (currencyData.by_currency && showBreakdown) {
      Object.entries(currencyData.by_currency).forEach(([currency, data]) => {
        if (data.total > 0) {
          const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
          breakdown.push({
            currency,
            amount: data.total,
            formatted: `${data.total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`,
            count: data.count
          });
        }
      });
    }

    return { primary: primaryAmount, breakdown };
  };

  /**
   * Calcule le montant converti d'un payin
   * @param {number} amount - Montant original
   * @param {number} exchangeRate - Taux de change
   * @returns {number} Montant converti
   */
  const calculateConvertedAmount = (amount, exchangeRate) => {
    if (!amount || !exchangeRate || exchangeRate === 0) return 0;
    return amount / exchangeRate;
  };

  /**
   * Formate les montants d'un payin (original + converti)
   * @param {Object} payin - Données du payin
   * @returns {Object} {original: Object, converted: Object}
   */
  const formatPayinAmounts = (payin) => {
    const commissionAmount = parseFloat(payin.commission_amount || 0);
    const taxes = parseFloat(payin.taxes || 0);
    const exchangeRate = parseFloat(payin.exchange_rate || 1);
    const originalCurrency = payin.original_currency || payin.currency || 'EUR';
    const baseCurrency = payin.currency || 'EUR';

    // Montants originaux
    const originalTotal = commissionAmount + taxes;
    const originalCurrencySymbol = originalCurrency === 'EUR' ? '€' : originalCurrency === 'USD' ? '$' : originalCurrency;

    // Montants convertis
    const convertedCommission = calculateConvertedAmount(commissionAmount, exchangeRate);
    const convertedTaxes = calculateConvertedAmount(taxes, exchangeRate);
    const convertedTotal = convertedCommission + convertedTaxes;
    const baseCurrencySymbol = baseCurrency === 'EUR' ? '€' : baseCurrency === 'USD' ? '$' : baseCurrency;

    return {
      original: {
        commission: commissionAmount,
        taxes: taxes,
        total: originalTotal,
        currency: originalCurrency,
        symbol: originalCurrencySymbol,
        formatted: {
          commission: `${commissionAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${originalCurrencySymbol}`,
          taxes: `${taxes.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${originalCurrencySymbol}`,
          total: `${originalTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${originalCurrencySymbol}`
        }
      },
      converted: {
        commission: convertedCommission,
        taxes: convertedTaxes,
        total: convertedTotal,
        currency: baseCurrency,
        symbol: baseCurrencySymbol,
        formatted: {
          commission: `${convertedCommission.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${baseCurrencySymbol}`,
          taxes: `${convertedTaxes.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${baseCurrencySymbol}`,
          total: `${convertedTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${baseCurrencySymbol}`
        }
      },
      exchangeRate: exchangeRate,
      needsConversion: originalCurrency !== baseCurrency && exchangeRate !== 1
    };
  };

  /**
   * Regroupe les payins par propriétaire et calcule les sous-totaux
   * @param {Array} payins - Liste des payins
   * @returns {Object} Payins groupés par owner avec sous-totaux
   */
  const groupPayinsByOwner = (payins) => {
    if (!payins || payins.length === 0) return {};

    const grouped = {};
    
    payins.forEach(payin => {
      const ownerId = payin.owner_id;
      const ownerName = payin.owner?.name || `Propriétaire #${ownerId}`;
      const ownerEmail = payin.owner?.email || '';
      
      if (!grouped[ownerId]) {
        grouped[ownerId] = {
          owner: {
            id: ownerId,
            name: ownerName,
            email: ownerEmail
          },
          payins: [],
          stats: {
            total_payins: 0,
            pending_count: 0,
            overdue_count: 0,
            completed_count: 0,
            total_original: {},
            total_converted: 0
          }
        };
      }
      
      grouped[ownerId].payins.push(payin);
      grouped[ownerId].stats.total_payins++;
      
      // Compter par statut
      if (payin.status === 'pending') grouped[ownerId].stats.pending_count++;
      else if (payin.status === 'overdue') grouped[ownerId].stats.overdue_count++;
      else if (payin.status === 'completed') grouped[ownerId].stats.completed_count++;
      
      // Calculer totaux
      const amounts = formatPayinAmounts(payin);
      
      // Total original par devise
      const origCurrency = amounts.original.currency;
      if (!grouped[ownerId].stats.total_original[origCurrency]) {
        grouped[ownerId].stats.total_original[origCurrency] = 0;
      }
      grouped[ownerId].stats.total_original[origCurrency] += amounts.original.total;
      
      // Total converti
      grouped[ownerId].stats.total_converted += amounts.converted.total;
    });
    
    // Arrondir les totaux convertis et formater les totaux par devise
    Object.keys(grouped).forEach(ownerId => {
      grouped[ownerId].stats.total_converted = Math.round(grouped[ownerId].stats.total_converted * 100) / 100;
      
      // Formater les totaux par devise
      Object.keys(grouped[ownerId].stats.total_original).forEach(currency => {
        grouped[ownerId].stats.total_original[currency] = Math.round(grouped[ownerId].stats.total_original[currency] * 100) / 100;
      });
    });
    
    return grouped;
  };

  /**
   * Formate les sous-totaux d'un propriétaire
   * @param {Object} ownerStats - Statistiques du propriétaire
   * @returns {string} Chaîne formatée des totaux
   */
  const formatOwnerTotals = (ownerStats) => {
    const originalTotals = Object.entries(ownerStats.total_original)
      .map(([currency, amount]) => {
        const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
        return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${symbol}`;
      })
      .join(' + ');
      
    const convertedTotal = `${ownerStats.total_converted.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
    
    if (originalTotals === convertedTotal) {
      return convertedTotal;
    } else {
      return `${originalTotals} → ${convertedTotal}`;
    }
  };

  /**
   * Génère un tooltip avec le détail des devises
   * @param {Object} currencyData - Données par devise 
   * @returns {string} Texte du tooltip
   */
  const generateCurrencyTooltip = (currencyData) => {
    if (!currencyData?.by_currency) return '';
    
    const details = Object.entries(currencyData.by_currency)
      .filter(([_, data]) => data.total > 0)
      .map(([currency, data]) => {
        const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
        return `${currency}: ${data.total.toFixed(2)} ${symbol} (${data.count} payin${data.count > 1 ? 's' : ''})`;
      });
    
    return details.length > 1 ? `Détail par devise:\n${details.join('\n')}` : '';
  };

  // ================================
  // Chargement initial et pagination infinie
  // ================================
  useEffect(() => {
    resetAndLoadData();
  }, [filters, sortBy, sortOrder]);

  /**
   * Remet à zéro les données et charge la première page
   */
  const resetAndLoadData = async () => {
    setLoadedPayins([]);
    setGroupedPayins({});
    setCurrentOffset(0);
    setHasMore(true);
    await loadData(true);
  };

  const loadData = useCallback(async (reset = false) => {
    try {
      console.log('� AdminPayins.loadData: REFRESH - Starting fresh reload', {
        filters,
        currentOffset,
        pageSize,
        sortBy,
        sortOrder,
        timestamp: new Date().toISOString(),
        forceRefresh: true
      });

      setLoading(true);
      setError(null);

      // Préparer les paramètres
      const filterParams = {
        startDate: filters.startDate?.format('YYYY-MM-DD'),
        endDate: filters.endDate?.format('YYYY-MM-DD'),
        ownerId: filters.ownerId || undefined,
        // Ajouter un timestamp pour éviter le cache
        _t: Date.now()
      };

      const payinParams = {
        ...filterParams,
        status: filters.status || undefined,
        minAmount: filters.minAmount || undefined,
        maxAmount: filters.maxAmount || undefined,
        overdue_only: filters.overdue_only,
        search: filters.search || undefined,
        sortBy,
        sortOrder,
        limit: pageSize,
        offset: reset ? 0 : currentOffset
      };

      console.log('📋 AdminPayins.loadData: REFRESH - Prepared params', {
        filterParams,
        payinParams,
        cacheBuster: filterParams._t
      });

      // Effacer les données précédentes
      setOverviewData(null);
      setPayinsData(null);

      console.log('🚀 AdminPayins.loadData: REFRESH - Making fresh API calls');
      
      // Charger les données en parallèle avec cache busting
      const [overviewResponse, payinsResponse] = await Promise.all([
        adminPayinService.getOverview(filterParams),
        adminPayinService.getPayins(payinParams)
      ]);

      console.log('📊 AdminPayins.loadData: REFRESH - Fresh API responses received', {
        overviewResponse,
        payinsResponse,
        overviewType: typeof overviewResponse,
        payinsType: typeof payinsResponse,
        overviewValid: !!overviewResponse,
        payinsValid: !!payinsResponse,
        payinsCount: payinsResponse?.payins?.length || 0,
        totalItems: payinsResponse?.pagination?.total_items || 0
      });

      setOverviewData(overviewResponse);
      
      // Gestion de l'infinite scroll : reset ou ajout de données
      if (reset) {
        // Reset complet : remplacer toutes les données
        setPayinsData(payinsResponse);
        setLoadedPayins(payinsResponse?.payins || []);
        setCurrentOffset(pageSize);
      } else {
        // Ajout de nouvelles données pour l'infinite scroll
        const newPayins = payinsResponse?.payins || [];
        setLoadedPayins(prev => [...prev, ...newPayins]);
        setCurrentOffset(prev => prev + pageSize);
        
        // Mettre à jour payinsData avec la pagination mise à jour
        setPayinsData(prev => ({
          ...prev,
          ...payinsResponse,
          payins: [...(prev?.payins || []), ...newPayins]
        }));
      }
      
      // Mettre à jour hasMore selon le nombre total d'éléments
      const totalItems = payinsResponse?.pagination?.total_items || 0;
      const currentLoadedCount = reset ? 
        (payinsResponse?.payins?.length || 0) : 
        (loadedPayins.length + (payinsResponse?.payins?.length || 0));
      setHasMore(currentLoadedCount < totalItems);

      console.log('✅ AdminPayins.loadData: REFRESH - State updated successfully', {
        overviewSet: !!overviewResponse,
        payinsSet: !!payinsResponse,
        isReset: reset,
        newPayinsCount: payinsResponse?.payins?.length || 0,
        totalLoadedPayins: currentLoadedCount,
        hasMore: currentLoadedCount < totalItems
      });

    } catch (error) {
      console.error('❌ AdminPayins.loadData: REFRESH - Error loading payins data:', {
        error,
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      setError('Erreur lors du chargement des données');
      // En cas d'erreur, vider les données pour forcer un vrai refresh
      setOverviewData(null);
      setPayinsData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, currentOffset, pageSize, sortBy, sortOrder]);

  // Fonction pour rafraîchissement manuel forcé
  const forceRefresh = useCallback(async () => {
    console.log('🔄 FORCE REFRESH: Manuel refresh triggered');
    await resetAndLoadData();
  }, []);

  /**
   * Charge plus de payins (pagination infinie)
   */
  const loadMorePayins = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    setLoadingMore(true);
    try {
      await loadData(false); // false = ajouter les données, ne pas reset
    } catch (error) {
      console.error('❌ Error loading more payins:', error);
      setError('Erreur lors du chargement des payins supplémentaires');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loadData]);

  /**
   * Toggle l'expansion d'un propriétaire
   */
  const toggleOwnerExpansion = (ownerId) => {
    setExpandedOwners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerId)) {
        newSet.delete(ownerId);
      } else {
        newSet.add(ownerId);
      }
      return newSet;
    });
  };

  // ================================
  // Handlers des filtres
  // ================================
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Les filtres triggent automatiquement resetAndLoadData via useEffect
  };

  const resetFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'day'),
      endDate: dayjs(),
      status: '',
      ownerId: '',
      minAmount: '',
      maxAmount: '',
      overdue_only: false,
      search: ''
    });
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  // ================================
  // Actions sur les payins
  // ================================
  const handleMarkAsPaid = async (payinId, paymentData) => {
    try {
      await adminPayinService.markAsPaid(payinId, paymentData);
      setPaymentDialog({ open: false, payin: null });
      loadData(); // Recharger les données
    } catch (error) {
      console.error('❌ Error marking payin as paid:', error);
      setError('Erreur lors du marquage comme payé');
    }
  };

  const handleAddNotes = async (payinId, notes) => {
    try {
      await adminPayinService.addNotes(payinId, notes);
      setNotesDialog({ open: false, payin: null, notes: '' });
      loadData(); // Recharger les données
    } catch (error) {
      console.error('❌ Error adding notes:', error);
      setError('Erreur lors de l\'ajout des notes');
    }
  };

  const handleCreateManual = async () => {
    try {
      const payinData = {
        ...manualPayinData,
        dueDate: manualPayinData.dueDate.format('YYYY-MM-DD'),
        commissionAmount: parseFloat(manualPayinData.commissionAmount),
        taxes: parseFloat(manualPayinData.taxes) || 0,
        cashAmountReceived: parseFloat(manualPayinData.cashAmountReceived) || null
      };

      await adminPayinService.createManual(payinData);
      setManualDialog({ open: false });
      resetManualPayinData();
      loadData(); // Recharger les données
    } catch (error) {
      console.error('❌ Error creating manual payin:', error);
      setError('Erreur lors de la création du payin manuel');
    }
  };

  const resetManualPayinData = () => {
    setManualPayinData({
      ownerId: '',
      reservationId: '',
      commissionAmount: '',
      taxes: '',
      currency: 'EUR',
      payinType: 'commission',
      dueDate: dayjs(),
      notes: '',
      cashAmountReceived: ''
    });
  };

  // ================================
  // Utilitaires d'affichage
  // ================================
  const getStatusIcon = (status) => {
    const icons = {
      pending: <PendingActionsIcon />,
      processing: <LoopIcon />,
      completed: <CheckCircleIcon />,
      overdue: <WarningIcon />,
      cancelled: <CancelIcon />
    };
    return icons[status] || <PendingActionsIcon />;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      overdue: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const calculateProgress = (data) => {
    if (!data?.summary?.total_payins) return 0;
    return Math.round((data.summary.completed_payins / data.summary.total_payins) * 100);
  };

  // ================================
  // RENDU DU COMPOSANT
  // ================================
  if (loading && !overviewData) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{ p: 3 }}>
        {/* En-tête avec titre et actions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AccountBalanceIcon sx={{ mr: 2, fontSize: 40 }} />
            Gestion des Payins
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Interface administrative pour la gestion des commissions dues par les propriétaires
          </Typography>

          {/* Boutons d'action */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {overviewData && `Dernière mise à jour: ${new Date().toLocaleTimeString()}`}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setManualDialog({ open: true })}
              >
                Créer Payin Manuel
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                endIcon={filterOpen ? <ExpandLess /> : <ExpandMore />}
                onClick={toggleFilter}
              >
                Filtres
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={forceRefresh}
                color="primary"
                disabled={loading}
                sx={{ 
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                {loading ? 'Actualisation...' : 'Actualiser'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Panneau de filtres */}
        <Collapse in={filterOpen}>
          <Paper sx={{ mb: 3, p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
            <Typography variant="h6" gutterBottom>
              Filtres et recherche
            </Typography>
            
            <Grid container spacing={3}>
              {/* Période */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date de début"
                  value={filters.startDate}
                  onChange={(newValue) => handleFilterChange('startDate', newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date de fin"
                  value={filters.endDate}
                  onChange={(newValue) => handleFilterChange('endDate', newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>

              {/* Statut */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filters.status}
                    label="Statut"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="processing">En traitement</MenuItem>
                    <MenuItem value="completed">Payé</MenuItem>
                    <MenuItem value="overdue">En retard</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Recherche */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Recherche (ID, nom, email)"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </Grid>

              {/* Montants */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Montant minimum (€)"
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Montant maximum (€)"
                  type="number"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                />
              </Grid>

              {/* ID Propriétaire */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID Propriétaire"
                  value={filters.ownerId}
                  onChange={(e) => handleFilterChange('ownerId', e.target.value)}
                />
              </Grid>

              {/* Options */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Retards uniquement</InputLabel>
                  <Select
                    value={filters.overdue_only}
                    label="Retards uniquement"
                    onChange={(e) => handleFilterChange('overdue_only', e.target.value)}
                  >
                    <MenuItem value={false}>Non</MenuItem>
                    <MenuItem value={true}>Oui</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={resetFilters}
                startIcon={<RefreshIcon />}
              >
                Réinitialiser
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {/* Affichage des erreurs */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Dashboard - Vue d'ensemble */}
        {overviewData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Métriques principales */}
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EuroIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Total Commissions</Typography>
                  </Box>
                  <Tooltip title={generateCurrencyTooltip(overviewData?.financial?.currencies_breakdown?.total_due)} arrow>
                    <Typography variant="h4">
                      {formatMultiCurrencyAmount(overviewData?.financial?.currencies_breakdown?.total_due).primary}
                    </Typography>
                  </Tooltip>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateProgress(overviewData)} 
                    sx={{ mt: 2, backgroundColor: 'rgba(255,255,255,0.2)' }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {Math.round((overviewData?.summary?.completion_rate || 0) * 100)}% traité
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">En retard</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData?.summary?.overdue_payins || 0}</Typography>
                  <Tooltip title={generateCurrencyTooltip(overviewData?.financial?.currencies_breakdown?.overdue)} arrow>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {formatMultiCurrencyAmount(overviewData?.financial?.currencies_breakdown?.overdue).primary}
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PendingActionsIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">En attente</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData?.summary?.pending_payins || 0}</Typography>
                  <Tooltip title={generateCurrencyTooltip(overviewData?.performance?.payins_by_status?.pending)} arrow>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {adminPayinService.formatAmount(overviewData?.performance?.payins_by_status?.pending?.amount_converted || 0)}
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Payés</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData?.summary?.completed_payins || 0}</Typography>
                  <Tooltip title={generateCurrencyTooltip(overviewData?.financial?.currencies_breakdown?.collected)} arrow>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {formatMultiCurrencyAmount(overviewData?.financial?.currencies_breakdown?.collected).primary}
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Répartition détaillée par devise */}
        {overviewData?.financial?.currencies_breakdown && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <AccountBalanceIcon sx={{ mr: 1 }} />
                Répartition par devise
              </Typography>
              
              <Grid container spacing={2}>
                {/* Total dû */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Total dû (En attente + En retard)
                    </Typography>
                    {overviewData.financial.currencies_breakdown.total_due?.by_currency && 
                      Object.entries(overviewData.financial.currencies_breakdown.total_due.by_currency).map(([currency, data]) => (
                        data.total > 0 && (
                          <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">
                              {currency} ({data.count} payin{data.count > 1 ? 's' : ''}):
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {data.total.toFixed(2)} {currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency}
                            </Typography>
                          </Box>
                        )
                      ))
                    }
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                      <Typography variant="body2">Total converti:</Typography>
                      <Typography variant="body2" color="primary.main">
                        {(overviewData.financial.currencies_breakdown.total_due.total_converted || 0).toFixed(2)} €
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* En retard */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      En retard
                    </Typography>
                    {overviewData.financial.currencies_breakdown.overdue?.by_currency && 
                      Object.entries(overviewData.financial.currencies_breakdown.overdue.by_currency).map(([currency, data]) => (
                        data.total > 0 && (
                          <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">
                              {currency} ({data.count} payin{data.count > 1 ? 's' : ''}):
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {data.total.toFixed(2)} {currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency}
                            </Typography>
                          </Box>
                        )
                      ))
                    }
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                      <Typography variant="body2">Total converti:</Typography>
                      <Typography variant="body2" color="error.main">
                        {(overviewData.financial.currencies_breakdown.overdue.total_converted || 0).toFixed(2)} €
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Collecté */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#e8f5e8' }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Payé (Collecté)
                    </Typography>
                    {overviewData.financial.currencies_breakdown.collected?.by_currency && 
                      Object.entries(overviewData.financial.currencies_breakdown.collected.by_currency).map(([currency, data]) => (
                        data.total > 0 && (
                          <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">
                              {currency} ({data.count} payin{data.count > 1 ? 's' : ''}):
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {data.total.toFixed(2)} {currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency}
                            </Typography>
                          </Box>
                        )
                      ))
                    }
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                      <Typography variant="body2">Total converti:</Typography>
                      <Typography variant="body2" color="success.main">
                        {(overviewData.financial.currencies_breakdown.collected.total_converted || 0).toFixed(2)} €
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Table des payins groupés par propriétaire */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Liste des Payins par Propriétaire ({loadedPayins.length} chargés)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {(() => {
                  const groupedData = groupPayinsByOwner(loadedPayins);
                  const ownerCount = Object.keys(groupedData).length;
                  return (
                    <>
                      {ownerCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {ownerCount} propriétaire{ownerCount > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  );
                })()}
                {loading && <CircularProgress size={24} />}
              </Box>
            </Box>

            {/* Affichage groupé par owner */}
            {(() => {
              const groupedData = groupPayinsByOwner(loadedPayins);
              return Object.values(groupedData).map((ownerGroup) => (
                <Card key={ownerGroup.owner.id} sx={{ mb: 2 }}>
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {ownerGroup.owner.name.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{ownerGroup.owner.name}</Typography>
                        <Chip 
                          label={`${ownerGroup.stats.total_payins} payin${ownerGroup.stats.total_payins > 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    subheader={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {ownerGroup.owner.email}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                          {formatOwnerTotals(ownerGroup.stats)}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {ownerGroup.stats.pending_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.pending_count} en attente`}
                              size="small"
                              color="warning"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                          {ownerGroup.stats.overdue_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.overdue_count} en retard`}
                              size="small"
                              color="error"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                          {ownerGroup.stats.completed_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.completed_count} payé${ownerGroup.stats.completed_count > 1 ? 's' : ''}`}
                              size="small"
                              color="success"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    action={
                      <IconButton 
                        onClick={() => toggleOwnerExpansion(ownerGroup.owner.id)}
                        aria-expanded={expandedOwners.has(ownerGroup.owner.id)}
                      >
                        {expandedOwners.has(ownerGroup.owner.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  />
                  <Collapse in={expandedOwners.has(ownerGroup.owner.id)} timeout="auto" unmountOnExit>
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Montant Original</TableCell>
                              <TableCell>Montant Converti (€)</TableCell>
                              <TableCell>Taux</TableCell>
                              <TableCell>Statut</TableCell>
                              <TableCell>Date création</TableCell>
                              <TableCell>Échéance</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ownerGroup.payins.map((payin) => (
                              <TableRow key={payin.id}>
                                <TableCell>
                                  <Chip 
                                    label={`#${payin.id}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const amounts = formatPayinAmounts(payin);
                                    return (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          {amounts.original.formatted.total}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Commission: {amounts.original.formatted.commission}
                                        </Typography>
                                        {amounts.original.taxes > 0 && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Taxes: {amounts.original.formatted.taxes}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const amounts = formatPayinAmounts(payin);
                                    return (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold" color={amounts.needsConversion ? 'primary' : 'text.primary'}>
                                          {amounts.converted.formatted.total}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Commission: {amounts.converted.formatted.commission}
                                        </Typography>
                                        {amounts.converted.taxes > 0 && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Taxes: {amounts.converted.formatted.taxes}
                                          </Typography>
                                        )}
                                        {amounts.needsConversion && (
                                          <Chip 
                                            label="Converti" 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                            sx={{ mt: 0.5, height: 16, fontSize: '0.6rem' }}
                                          />
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const amounts = formatPayinAmounts(payin);
                                    return amounts.needsConversion ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          {amounts.exchangeRate.toFixed(4)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {amounts.original.currency} → {amounts.converted.currency}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        1.0000
                                      </Typography>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={getStatusIcon(payin.status)}
                                    label={adminPayinService.getStatusLabel(payin.status)}
                                    color={getStatusColor(payin.status)}
                                    size="small"
                                  />
                                  {payin.status === 'overdue' && payin.due_date && (
                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                      {adminPayinService.calculateDaysOverdue(payin.due_date)} jours de retard
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {adminPayinService.formatDate(payin.created_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2"
                                    color={payin.status === 'overdue' ? 'error' : 'text.primary'}
                                  >
                                    {adminPayinService.formatDate(payin.due_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {payin.status !== 'completed' && (
                                      <Tooltip title="Marquer comme payé">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPaymentDialog({ open: true, payin })}
                                        >
                                          <PaymentIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Ajouter/Modifier notes">
                                      <IconButton
                                        size="small"
                                        onClick={() => setNotesDialog({ 
                                          open: true, 
                                          payin, 
                                          notes: payin.admin_notes || '' 
                                        })}
                                      >
                                        <NotesIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Collapse>
                </Card>
              ));
            })()}

            {/* Load More Button for Infinite Scroll */}
            {hasMore && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={loadMorePayins}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? 'Chargement...' : 'Charger plus de payins'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Dialog - Marquer comme payé */}
        <Dialog 
          open={paymentDialog.open} 
          onClose={() => setPaymentDialog({ open: false, payin: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Marquer le payin #{paymentDialog.payin?.id} comme payé
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Référence du paiement"
                  placeholder="ex: TRANSFER_20240101_001"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select defaultValue="manual" label="Méthode de paiement">
                    <MenuItem value="manual">Manuel</MenuItem>
                    <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
                    <MenuItem value="cash">Espèces</MenuItem>
                    <MenuItem value="check">Chèque</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes administratives"
                  multiline
                  rows={3}
                  placeholder="Notes sur ce paiement..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialog({ open: false, payin: null })}>
              Annuler
            </Button>
            <Button variant="contained" onClick={() => handleMarkAsPaid(paymentDialog.payin?.id, {})}>
              Confirmer le paiement
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog - Notes */}
        <Dialog 
          open={notesDialog.open} 
          onClose={() => setNotesDialog({ open: false, payin: null, notes: '' })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Notes pour le payin #{notesDialog.payin?.id}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Notes administratives"
              value={notesDialog.notes}
              onChange={(e) => setNotesDialog(prev => ({ ...prev, notes: e.target.value }))}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotesDialog({ open: false, payin: null, notes: '' })}>
              Annuler
            </Button>
            <Button 
              variant="contained" 
              onClick={() => handleAddNotes(notesDialog.payin?.id, notesDialog.notes)}
            >
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog - Créer payin manuel */}
        <Dialog 
          open={manualDialog.open} 
          onClose={() => setManualDialog({ open: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Créer un payin manuel</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Propriétaire"
                  required
                  value={manualPayinData.ownerId}
                  onChange={(e) => setManualPayinData(prev => ({ ...prev, ownerId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Réservation (optionnel)"
                  value={manualPayinData.reservationId}
                  onChange={(e) => setManualPayinData(prev => ({ ...prev, reservationId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Montant commission"
                  type="number"
                  required
                  value={manualPayinData.commissionAmount}
                  onChange={(e) => setManualPayinData(prev => ({ ...prev, commissionAmount: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Taxes"
                  type="number"
                  value={manualPayinData.taxes}
                  onChange={(e) => setManualPayinData(prev => ({ ...prev, taxes: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type de payin</InputLabel>
                  <Select
                    value={manualPayinData.payinType}
                    label="Type de payin"
                    onChange={(e) => setManualPayinData(prev => ({ ...prev, payinType: e.target.value }))}
                  >
                    <MenuItem value="commission">Commission</MenuItem>
                    <MenuItem value="penalty">Pénalité</MenuItem>
                    <MenuItem value="adjustment">Ajustement</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date d'échéance"
                  value={manualPayinData.dueDate}
                  onChange={(newValue) => setManualPayinData(prev => ({ ...prev, dueDate: newValue }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={manualPayinData.notes}
                  onChange={(e) => setManualPayinData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualDialog({ open: false })}>
              Annuler
            </Button>
            <Button variant="contained" onClick={handleCreateManual}>
              Créer le payin
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminPayins;